import * as React from "react";

import { Button } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
export interface InputProps extends React.ComponentProps<"input"> {
  className?: string;
  innerRef?: React.Ref<HTMLInputElement>;
  dataTestId?: string;
}

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  if (ref) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, innerRef, type, ...props }: InputProps, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "border-input bg-card ring-offset-background file:text-foreground placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        ref={(element) => {
          assignRef(ref, element);
          assignRef(innerRef, element);
        }}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };

const PasswordInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          className={cn("hide-password-toggle pr-10", className)}
          ref={ref}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
          onClick={() => {
            setShowPassword((prev) => !prev);
          }}
        >
          {showPassword ? (
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <EyeOffIcon className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="sr-only">
            {showPassword ? "Hide password" : "Show password"}
          </span>
        </Button>

        {/* hides browsers password toggles */}
        <style>{`
					.hide-password-toggle::-ms-reveal,
					.hide-password-toggle::-ms-clear {
						visibility: hidden;
						pointer-events: none;
						display: none;
					}
				`}</style>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };

const PhoneNumberInput = ({
  className,
  innerRef,
  ...props
}: {
  className?: string;
  value: string;
  onChange: (value?: string) => void;
  innerRef: React.Ref<HTMLInputElement>;
  displayName?: string;
}) => {
  return (
    <PhoneInput
      className={className}
      international
      countryCallingCodeEditable={false}
      defaultCountry="US"
      inputComponent={Input}
      numberInputProps={{ innerRef }}
      {...props}
    />
  );
};

export { PhoneNumberInput };
